import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '@/components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { ArrowLeft, Star, Plus, Send, Trash2, AlertTriangle, MessageSquare, Loader2, Flag } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const STATUS_LABELS = {
  new: { label: 'Nouveau', cls: 'bg-amber-50 text-amber-700 border-amber-200' },
  responded: { label: 'Repondu', cls: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
  flagged: { label: 'Signale', cls: 'bg-red-50 text-[#993333] border-red-200' },
};

function Stars({ count, size = 'w-4 h-4' }) {
  return (
    <div className="flex gap-0.5">
      {[1, 2, 3, 4, 5].map(i => (
        <Star key={i} className={`${size} ${i <= count ? 'fill-[#C4A962] text-[#C4A962]' : 'text-gray-200'}`} strokeWidth={1.5} />
      ))}
    </div>
  );
}

function StarPicker({ value, onChange }) {
  return (
    <div className="flex gap-1">
      {[1, 2, 3, 4, 5].map(i => (
        <button key={i} type="button" onClick={() => onChange(i)} className="hover:scale-110 transition-transform" data-testid={`star-pick-${i}`}>
          <Star className={`w-6 h-6 ${i <= value ? 'fill-[#C4A962] text-[#C4A962]' : 'text-gray-300 hover:text-[#C4A962]/50'}`} strokeWidth={1.5} />
        </button>
      ))}
    </div>
  );
}

function DistributionBar({ distribution, total }) {
  if (!total) return null;
  return (
    <div className="space-y-1.5">
      {[5, 4, 3, 2, 1].map(n => {
        const count = distribution?.[String(n)] || 0;
        const pct = total > 0 ? (count / total) * 100 : 0;
        return (
          <div key={n} className="flex items-center gap-2 text-xs">
            <span className="w-3 text-right font-medium text-[#595959]">{n}</span>
            <Star className="w-3 h-3 fill-[#C4A962] text-[#C4A962]" />
            <div className="flex-1 h-2 bg-[#F5F5F0] rounded-full overflow-hidden">
              <div className="h-full bg-[#C4A962] rounded-full transition-all" style={{ width: `${pct}%` }} />
            </div>
            <span className="w-6 text-[#595959]">{count}</span>
          </div>
        );
      })}
    </div>
  );
}

export default function AdminReviews() {
  const navigate = useNavigate();
  const token = localStorage.getItem('admin_token');
  const [reviews, setReviews] = useState([]);
  const [stats, setStats] = useState(null);
  const [statusFilter, setStatusFilter] = useState('all');
  const [ratingFilter, setRatingFilter] = useState('all');
  const [showAdd, setShowAdd] = useState(false);
  const [newReview, setNewReview] = useState({ author_name: '', rating: 5, text: '', date: '', source: 'google' });
  const [replyFor, setReplyFor] = useState(null);
  const [replyText, setReplyText] = useState('');
  const [saving, setSaving] = useState(false);

  const api = useCallback((method, url, data) =>
    axios({ method, url: `${BACKEND_URL}${url}`, data, headers: { Authorization: `Bearer ${token}` } }),
  [token]);

  const load = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (statusFilter !== 'all') params.set('status', statusFilter);
      if (ratingFilter !== 'all') params.set('rating_filter', ratingFilter);
      const [revRes, stRes] = await Promise.all([
        api('get', `/api/admin/reviews?${params}`),
        api('get', '/api/admin/reviews/stats'),
      ]);
      setReviews(revRes.data);
      setStats(stRes.data);
    } catch { /* ignore */ }
  }, [api, statusFilter, ratingFilter]);

  useEffect(() => {
    if (!token) { navigate('/admin'); return; }
    load();
  }, [token, navigate, load]);

  const addReview = async () => {
    if (!newReview.author_name.trim()) { toast.error('Le nom est requis'); return; }
    setSaving(true);
    try {
      await api('post', '/api/admin/reviews', newReview);
      setShowAdd(false);
      setNewReview({ author_name: '', rating: 5, text: '', date: '', source: 'google' });
      toast.success('Avis ajoute');
      load();
    } catch { toast.error('Erreur'); }
    setSaving(false);
  };

  const sendReply = async () => {
    if (!replyText.trim()) return;
    setSaving(true);
    try {
      await api('put', `/api/admin/reviews/${replyFor.id}`, { response_text: replyText });
      setReplyFor(null);
      setReplyText('');
      toast.success('Reponse enregistree');
      load();
    } catch { toast.error('Erreur'); }
    setSaving(false);
  };

  const toggleFlag = async (r) => {
    const newStatus = r.status === 'flagged' ? 'new' : 'flagged';
    try {
      await api('put', `/api/admin/reviews/${r.id}`, { status: newStatus });
      toast.success(newStatus === 'flagged' ? 'Avis signale' : 'Signalement retire');
      load();
    } catch { toast.error('Erreur lors du signalement'); }
  };

  const deleteReview = async (id) => {
    if (!window.confirm('Supprimer cet avis ?')) return;
    try {
      await api('delete', `/api/admin/reviews/${id}`);
      toast.success('Avis supprime');
      load();
    } catch { toast.error('Erreur'); }
  };

  const logout = () => { localStorage.removeItem('admin_token'); localStorage.removeItem('admin_email'); navigate('/admin'); };

  return (
    <div className="min-h-screen bg-[#FAFAF9]">
      <header className="bg-[#2C3E30] text-[#F2F0E9] px-6 md:px-12 py-4">
        <div className="max-w-[1200px] mx-auto flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button onClick={() => navigate('/admin/dashboard')} className="text-[#F2F0E9]/70 hover:text-[#F2F0E9]" data-testid="reviews-back-btn">
              <ArrowLeft className="w-5 h-5" strokeWidth={1.5} />
            </button>
            <h1 className="font-['Playfair_Display'] text-lg md:text-xl flex items-center gap-2">
              <Star className="w-5 h-5" strokeWidth={1.5} /> Gestion des avis
            </h1>
          </div>
          <div className="flex items-center gap-2">
            <Button onClick={() => setShowAdd(true)} size="sm" className="bg-[#C4A962] hover:bg-[#b5945a] text-white" data-testid="add-review-btn">
              <Plus className="w-4 h-4 mr-1" /> Ajouter un avis
            </Button>
            <Button onClick={logout} variant="ghost" className="text-[#F2F0E9]/70 hover:text-[#F2F0E9] hover:bg-white/10" size="sm">Deconnexion</Button>
          </div>
        </div>
      </header>

      <div className="max-w-[1200px] mx-auto px-6 md:px-12 py-8 space-y-6">

        {/* Stats cards */}
        {stats && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-testid="reviews-stats">
            <div className="bg-white rounded-xl p-6 shadow-[0_2px_8px_-2px_rgba(0,0,0,0.05)] flex items-center gap-4">
              <div className="w-16 h-16 rounded-full bg-[#C4A962]/10 flex items-center justify-center">
                <span className="text-2xl font-bold text-[#C4A962]" data-testid="avg-rating">{stats.average || '-'}</span>
              </div>
              <div>
                <Stars count={Math.round(stats.average || 0)} />
                <p className="text-xs text-[#595959] mt-1">{stats.total} avis au total</p>
              </div>
            </div>
            <div className="bg-white rounded-xl p-6 shadow-[0_2px_8px_-2px_rgba(0,0,0,0.05)]">
              <DistributionBar distribution={stats.distribution} total={stats.total} />
            </div>
            <div className="bg-white rounded-xl p-6 shadow-[0_2px_8px_-2px_rgba(0,0,0,0.05)]">
              <div className="space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-[#595959]">Nouveaux</span>
                  <Badge className="bg-amber-50 text-amber-700 border-amber-200 border" data-testid="stat-new">{stats.new_count}</Badge>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-[#595959]">Repondus</span>
                  <Badge className="bg-emerald-50 text-emerald-700 border-emerald-200 border" data-testid="stat-responded">{stats.responded_count}</Badge>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-[#595959]">Signales</span>
                  <Badge className="bg-red-50 text-[#993333] border-red-200 border" data-testid="stat-flagged">{stats.flagged_count}</Badge>
                </div>
                {stats.negative_unresponded > 0 && (
                  <div className="flex items-center gap-2 mt-3 p-2 rounded-lg bg-red-50 text-[#993333] text-xs font-medium" data-testid="negative-alert">
                    <AlertTriangle className="w-4 h-4" />
                    {stats.negative_unresponded} avis negatif{stats.negative_unresponded > 1 ? 's' : ''} sans reponse
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Filters */}
        <div className="flex flex-wrap gap-3 items-center" data-testid="reviews-filters">
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-[160px] bg-white" data-testid="filter-status"><SelectValue placeholder="Statut" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tous les statuts</SelectItem>
              <SelectItem value="new">Nouveaux</SelectItem>
              <SelectItem value="responded">Repondus</SelectItem>
              <SelectItem value="flagged">Signales</SelectItem>
            </SelectContent>
          </Select>
          <Select value={ratingFilter} onValueChange={setRatingFilter}>
            <SelectTrigger className="w-[160px] bg-white" data-testid="filter-rating"><SelectValue placeholder="Note" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Toutes les notes</SelectItem>
              <SelectItem value="positive">4-5 etoiles</SelectItem>
              <SelectItem value="neutral">3 etoiles</SelectItem>
              <SelectItem value="negative">1-2 etoiles</SelectItem>
            </SelectContent>
          </Select>
          <span className="text-sm text-[#595959]">{reviews.length} avis</span>
        </div>

        {/* Reviews list */}
        <div className="space-y-4" data-testid="reviews-list">
          {reviews.length === 0 ? (
            <div className="bg-white rounded-xl p-12 shadow-[0_2px_8px_-2px_rgba(0,0,0,0.05)] text-center">
              <Star className="w-10 h-10 text-gray-200 mx-auto mb-3" />
              <p className="text-[#595959]">Aucun avis pour le moment</p>
              <p className="text-xs text-[#595959] mt-1">Cliquez sur "Ajouter un avis" pour en saisir un</p>
            </div>
          ) : reviews.map(r => (
            <div
              key={r.id}
              className={`bg-white rounded-xl p-5 shadow-[0_2px_8px_-2px_rgba(0,0,0,0.05)] ${r.rating <= 2 && r.status !== 'responded' ? 'ring-1 ring-red-200' : ''}`}
              data-testid={`review-${r.id}`}
            >
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-1">
                    <span className="font-semibold text-[#2C3E30]" data-testid={`review-author-${r.id}`}>{r.author_name}</span>
                    <Stars count={r.rating} />
                    <Badge className={`text-[10px] border ${STATUS_LABELS[r.status]?.cls || STATUS_LABELS.new.cls}`} data-testid={`review-status-${r.id}`}>
                      {STATUS_LABELS[r.status]?.label || r.status}
                    </Badge>
                    <span className="text-xs text-[#595959]">{r.date}</span>
                    {r.source && <Badge variant="outline" className="text-[10px] text-[#595959]">{r.source}</Badge>}
                  </div>
                  <p className="text-sm text-[#333] leading-relaxed mt-2">{r.text || <span className="italic text-[#595959]">Aucun commentaire</span>}</p>
                  {r.response_text && (
                    <div className="mt-3 p-3 bg-[#F5F5F0] rounded-lg border-l-3 border-[#8A9A5B]" data-testid={`review-response-${r.id}`}>
                      <p className="text-xs font-medium text-[#2C3E30] mb-1">Votre reponse ({r.response_date})</p>
                      <p className="text-sm text-[#595959]">{r.response_text}</p>
                    </div>
                  )}
                </div>
                <div className="flex gap-1 flex-shrink-0">
                  <Button size="sm" variant="ghost" onClick={() => { setReplyFor(r); setReplyText(r.response_text || ''); }} className="text-[#8A9A5B] hover:text-[#7a8a4d]" data-testid={`reply-btn-${r.id}`}>
                    <MessageSquare className="w-4 h-4" />
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => toggleFlag(r)} className={r.status === 'flagged' ? 'text-[#993333]' : 'text-[#595959]'} data-testid={`flag-btn-${r.id}`}>
                    <Flag className="w-4 h-4" />
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => deleteReview(r.id)} className="text-[#595959] hover:text-[#993333]" data-testid={`delete-btn-${r.id}`}>
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Add review dialog */}
      <Dialog open={showAdd} onOpenChange={setShowAdd}>
        <DialogContent className="sm:max-w-md" data-testid="add-review-dialog">
          <DialogHeader><DialogTitle>Ajouter un avis</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="text-sm font-medium text-[#2C3E30] block mb-1">Auteur *</label>
              <Input value={newReview.author_name} onChange={e => setNewReview({ ...newReview, author_name: e.target.value })} placeholder="Nom de l'auteur" data-testid="review-author-input" />
            </div>
            <div>
              <label className="text-sm font-medium text-[#2C3E30] block mb-1">Note</label>
              <StarPicker value={newReview.rating} onChange={r => setNewReview({ ...newReview, rating: r })} />
            </div>
            <div>
              <label className="text-sm font-medium text-[#2C3E30] block mb-1">Commentaire</label>
              <Textarea value={newReview.text} onChange={e => setNewReview({ ...newReview, text: e.target.value })} placeholder="Texte de l'avis..." rows={3} data-testid="review-text-input" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-sm font-medium text-[#2C3E30] block mb-1">Date</label>
                <Input type="date" value={newReview.date} onChange={e => setNewReview({ ...newReview, date: e.target.value })} data-testid="review-date-input" />
              </div>
              <div>
                <label className="text-sm font-medium text-[#2C3E30] block mb-1">Source</label>
                <Select value={newReview.source} onValueChange={v => setNewReview({ ...newReview, source: v })}>
                  <SelectTrigger data-testid="review-source-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="google">Google</SelectItem>
                    <SelectItem value="booking">Booking</SelectItem>
                    <SelectItem value="tripadvisor">TripAdvisor</SelectItem>
                    <SelectItem value="autre">Autre</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAdd(false)}>Annuler</Button>
            <Button onClick={addReview} disabled={saving} className="bg-[#8A9A5B] hover:bg-[#7a8a4d] text-white" data-testid="confirm-add-review">
              {saving ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Plus className="w-4 h-4 mr-1" />} Ajouter
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reply dialog */}
      <Dialog open={!!replyFor} onOpenChange={() => setReplyFor(null)}>
        <DialogContent className="sm:max-w-lg" data-testid="reply-dialog">
          <DialogHeader><DialogTitle>Repondre a {replyFor?.author_name}</DialogTitle></DialogHeader>
          {replyFor && (
            <div className="space-y-4">
              <div className="p-3 bg-[#F5F5F0] rounded-lg">
                <div className="flex items-center gap-2 mb-1">
                  <Stars count={replyFor.rating} size="w-3 h-3" />
                  <span className="text-xs text-[#595959]">{replyFor.date}</span>
                </div>
                <p className="text-sm text-[#333]">{replyFor.text || 'Aucun commentaire'}</p>
              </div>
              <div>
                <label className="text-sm font-medium text-[#2C3E30] block mb-1">Votre reponse</label>
                <Textarea value={replyText} onChange={e => setReplyText(e.target.value)} placeholder="Redigez votre reponse..." rows={4} data-testid="reply-text-input" />
                <p className="text-xs text-[#595959] mt-1">Cette reponse sera enregistree dans votre suivi. Pensez a la copier sur Google Maps.</p>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setReplyFor(null)}>Annuler</Button>
            <Button onClick={sendReply} disabled={saving || !replyText.trim()} className="bg-[#2C3E30] hover:bg-[#3d5240] text-white" data-testid="confirm-reply">
              {saving ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Send className="w-4 h-4 mr-1" />} Enregistrer la reponse
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
